import React, { useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";

const ITEM_H = 44; // px

/**
 * Sélecteur à molette (âge, taille, poids). Défilement vertical avec
 * accroche (snap) et surbrillance centrale rouge.
 */
export const WheelPicker = ({
    options = [],
    value,
    onChange,
    unit = "",
    className,
    dataTestId = "wheel-picker",
}) => {
    const ref = useRef(null);
    const timer = useRef(null);

    const scrollToIndex = useCallback((idx, smooth = true) => {
        const el = ref.current;
        if (!el) return;
        el.scrollTo({ top: idx * ITEM_H, behavior: smooth ? "smooth" : "auto" });
    }, []);

    useEffect(() => {
        const idx = options.indexOf(value);
        if (idx >= 0) scrollToIndex(idx, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleScroll = () => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            const el = ref.current;
            if (!el) return;
            const idx = Math.round(el.scrollTop / ITEM_H);
            const clamped = Math.max(0, Math.min(options.length - 1, idx));
            if (options[clamped] !== value) onChange && onChange(options[clamped]);
            scrollToIndex(clamped);
        }, 90);
    };

    return (
        <div
            data-testid={dataTestId}
            className={cn(
                "relative h-[220px] w-full rounded-xl bg-ink-900 border border-ink-700 overflow-hidden",
                className,
            )}
        >
            {/* surbrillance centrale */}
            <div
                className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[44px] bg-ink-800 border-y border-revred/60 z-10"
                aria-hidden
            />
            <div
                ref={ref}
                onScroll={handleScroll}
                className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-none relative z-20"
                style={{ paddingTop: 88, paddingBottom: 88 }}
            >
                {options.map((opt) => {
                    const active = opt === value;
                    return (
                        <div
                            key={opt}
                            onClick={() => {
                                onChange && onChange(opt);
                                scrollToIndex(options.indexOf(opt));
                            }}
                            className={cn(
                                "h-[44px] snap-center flex items-center justify-center select-none tap-feedback transition-transform",
                                active
                                    ? "text-white font-display font-black text-4xl scale-105"
                                    : "text-revtext-secondary font-body text-lg",
                            )}
                            data-testid={`${dataTestId}-option-${opt}`}
                        >
                            {opt}
                            {unit ? <span className="ml-1 text-base opacity-70">{unit}</span> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
